import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SupplierRequestV1Service } from 'src/app/api/services';
import { catchError, exhaustMap, firstValueFrom, from, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  loadRequest,
  loadRequestFailed,
  loadRequestSuccess,
  saveExternal,
  saveExternalFailed,
  saveExternalSuccess,
  saveInternal,
  saveInternalFailed,
  saveInternalSuccess,
  submitForExternalReview,
  submitForExternalReviewFailed,
  submitForExternalReviewSuccess,
  submitForInternalReview,
  submitForInternalReviewFailed,
  submitForInternalReviewSuccess,
  updateErpSyncStatus,
  updateErpSyncStatusFailed,
  updateErpSyncStatusSuccess,
} from '../../actions/request-form/request-form.actions';
import { selectUserRole, selectUserSupplierV1 } from '../../../../authentication/state/selector';
import { EcdhCryptoService, EncryptedPayload } from '../../../../shared/crypto/ecdh-crypto.service';
import { SupplierRequestDetailV1 } from '../../../../api/models';

@Injectable()
export class RequestFormEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly api: SupplierRequestV1Service,
    private readonly store: Store,
    private readonly ecdhCrypto: EcdhCryptoService
  ) {}

  loadRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRequest),
      withLatestFrom(this.store.select(selectUserRole), this.store.select(selectUserSupplierV1)),
      switchMap(([{ requestId }, role, supplier]) => {
        const isSupplierUser = role === 'supplier' && !!supplier?.id;

        if (isSupplierUser) {
          return from(this.loadRequestForSupplier(requestId));
        }

        return this.api.getSupplierRequestById({ requestId }).pipe(
          map((request) => loadRequestSuccess({ requestId, request })),
          catchError((error) => of(loadRequestFailed({ error })))
        );
      })
    )
  );

  private async loadRequestForSupplier(requestId: string) {
    try {
      const { headerValue, privateKey } = await this.ecdhCrypto.getClientPublicKeyHeader();
      const request = await firstValueFrom(
        this.api.getSupplierRequestByIdOwn({
          requestId,
          'X-Client-Public-Key': headerValue,
        })
      );

      if (!request) {
        return loadRequestFailed({ error: 'Request not found' });
      }

      const decryptedRequest = await this.decryptRequestDataIfNeeded(request, privateKey);
      return loadRequestSuccess({ requestId, request: decryptedRequest });
    } catch (error) {
      return loadRequestFailed({ error });
    }
  }

  private async decryptRequestDataIfNeeded(
    request: SupplierRequestDetailV1,
    privateKey: CryptoKey
  ): Promise<SupplierRequestDetailV1> {
    const result = { ...request };

    if (result.internalData && this.ecdhCrypto.isEncryptedPayload(result.internalData)) {
      result.internalData = await this.ecdhCrypto.decryptPayload(result.internalData as EncryptedPayload, privateKey);
    }

    if (result.notesData && this.ecdhCrypto.isEncryptedPayload(result.notesData)) {
      result.notesData = await this.ecdhCrypto.decryptPayload(result.notesData as EncryptedPayload, privateKey);
    }

    return result;
  }

  saveInternal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveInternal),
      switchMap(({ requestId, body }) =>
        this.api.saveInternalData({ requestId, body }).pipe(
          map(() => saveInternalSuccess({ requestId })),
          catchError((error) => of(saveInternalFailed({ error })))
        )
      )
    )
  );

  submitInternal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitForInternalReview),
      switchMap(({ requestId, body }) =>
        this.api.submitInternalRequestData({ requestId, body }).pipe(
          map(() => submitForInternalReviewSuccess({ requestId })),
          catchError((error) => of(submitForInternalReviewFailed({ error })))
        )
      )
    )
  );

  saveExternal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveExternal),
      switchMap(({ requestId, body }) =>
        this.api.saveExternalData({ requestId, body }).pipe(
          map(() => saveExternalSuccess({ requestId })),
          catchError((error) => of(saveExternalFailed({ error })))
        )
      )
    )
  );

  submitExternal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitForExternalReview),
      switchMap(({ requestId, body }) =>
        this.api.submitExternalRequestData({ requestId, body }).pipe(
          map(() => submitForExternalReviewSuccess({ requestId })),
          catchError((error) => of(submitForExternalReviewFailed({ error })))
        )
      )
    )
  );

  updateErpSyncStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateErpSyncStatus),
      // A mutation: ignore repeat clicks while one update is in flight instead of cancelling it.
      exhaustMap(({ requestId, body }) =>
        this.api.updateRequestErpSyncStatus({ requestId, body }).pipe(
          map((request) => updateErpSyncStatusSuccess({ requestId, request })),
          catchError((error) => of(updateErpSyncStatusFailed({ error })))
        )
      )
    )
  );
}
